import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Typography,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import BrandInfo from './BrandInfo';
import LabeledTextList, { LabeledTextListItem } from '@/components/common/LabeledTextList';
import { useLoaderData, useParams, Await, useRevalidator } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import { ModeEdit as EditIcon, Info as InfoIcon } from '@mui/icons-material';

import { getMallInfo, MallInfoResponse } from '@/services/mall.service';
import { dialogOpen } from '@/modules/MuiDialog';
import { normalizeOnlineSalesNumber } from './BusinessInfoDialog';
import { AccountInfoDialog } from './AccountInfoDialog';
import { ReturnAddressInfoDialog } from './ReturnAddressInfoDialog';
import TaxInfoDialog from './TaxInfoDialog';
import { ManagerPhoneDialog } from './ManagerPhoneDialog';
import { SingleFieldDialog } from './SingleFieldDialog';
import { CertFieldDialog } from './CertFieldDialog';
import sessionStore from '@/store/modules/sessionStore';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSpaceUserV2Store } from '@/store';
import CustomerCareBanner from './CustomerCareBanner';

/**
 * 항목 값 + 우측 연필. 연필을 누르면 그 항목 전용 다이얼로그가 열린다(한 항목씩 수정).
 * 계좌 3종은 실명인증이 세트로 걸려 있어 연필을 눌러도 계좌 다이얼로그(3종 묶음)가 열린다.
 */
const EditableValue = ({ value, onOpen }: { value?: React.ReactNode; onOpen: () => void }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>{value || '-'}</span>
        <IconButton size="small" onClick={onOpen}>
            <EditIcon fontSize="small" />
        </IconButton>
    </Box>
);

const BusinessInfo = () => {
    const { t } = useTranslation();
    const { brandInfoData, businessData, customerSummary }: any = useLoaderData();
    const { revalidate, state } = useRevalidator();
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';
    const [brandInfo, setBrandInfo] = useState<MallInfoResponse>(brandInfoData);
    const [isDispatching, setIsDispatching] = useState(false);
    const { mallId } = useParams();

    // 한 항목씩 수정 — 다이얼로그를 열고, 닫히면 화면 갱신
    const openEditDialog = (innerElement: JSX.Element) => {
        dialogOpen({
            onExited: () => {
                revalidate();
            },
            innerElement,
        });
    };
    // 계좌 3종(은행/계좌번호/예금주)은 계좌 실명인증이 세트로 걸려 있어, 어느 항목을 눌러도 계좌 다이얼로그를 연다.
    const openAccountDialog = (businessData: any) => {
        const { userId } = sessionStore.getState();
        if (!userId) {
            toast.error(t('common.userInfoNotFound'));
            return;
        }
        openEditDialog(<AccountInfoDialog mallId={mallId || ''} {...businessData} userId={userId} />);
    };

    useEffect(() => {
        setBrandInfo(brandInfoData);
    }, [brandInfoData]);

    const dispatchBrandInfo = async () => {
        setIsDispatching(true);
        try {
            const brandInfo = await getMallInfo(mallId || '');
            setBrandInfo(brandInfo);
        } catch (e) {
            console.error(e);
        } finally {
            setIsDispatching(false);
        }
    };
    return (
        <div className={'flex flex-col flex-1'}>
            <Grid component={'section'} container>
                <Grid xs={12}>
                    <Typography variant={'h1'} gutterBottom>
                        {t('mall.sellerInfo')}
                    </Typography>
                    {/* 계좌·사업자 정보는 항목 옆 연필로 하나씩 수정한다 */}
                    <Typography variant={'body2'} color={'text.secondary'} sx={{ mb: 1 }}>
                        {t('mall.editOneItemAtATime')}
                    </Typography>
                </Grid>
                {/* 고객관리 바로가기 배너 — 소도몰 역할에서는 고객관리 메뉴가 숨겨지므로 미노출 */}
                {!isSodomall && (
                    <Grid xs={12}>
                        <CustomerCareBanner summary={customerSummary} />
                    </Grid>
                )}
                <Grid xs={12} md={6}>
                    <BrandInfo default={brandInfo} dispatchDefault={dispatchBrandInfo} />
                </Grid>
                <Grid xs={12} md={6}>
                    <Grid xs={12}>
                        <Suspense fallback={<div>loading...</div>}>
                            <Await resolve={businessData}>
                                {(businessData) => {
                                    const isBlurData = businessData.mallOpenBusinessType === 'PLATFORM';
                                    return (
                                        <Card>
                                            <div className="flex">
                                                <CardHeader title={<p>{t('mall.accountInfo')}</p>} />
                                            </div>
                                            <CardContent>
                                                <LabeledTextList>
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.bank')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData?.settleBank}
                                                                onOpen={() => openAccountDialog(businessData)}
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.accountNumber')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData?.settleAccountNumber}
                                                                onOpen={() => openAccountDialog(businessData)}
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        label={t('mall.accountHolder')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData?.settleAccountOwnerName}
                                                                onOpen={() => openAccountDialog(businessData)}
                                                            />
                                                        }
                                                    />
                                                </LabeledTextList>
                                            </CardContent>
                                            <Divider />
                                            <div className="flex">
                                                <CardHeader title={<p>{t('mall.businessInfo')}</p>} />
                                            </div>
                                            <CardContent>
                                                <LabeledTextList>
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.companyName')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.companyName}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <SingleFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'companyName'}
                                                                            label={t('mall.companyName')}
                                                                            value={businessData.companyName}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.ownerName')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.ownerName}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <SingleFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'ownerName'}
                                                                            label={t('mall.ownerName')}
                                                                            value={businessData.ownerName}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.businessRegistrationNumber')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.businessRegistrationNumber}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <CertFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'businessRegistrationNumber'}
                                                                            label={t('mall.businessRegistrationNumber')}
                                                                            value={businessData.businessRegistrationNumber}
                                                                            certField={'businessRegistrationCertificateUrl'}
                                                                            certLabel={t('mall.businessRegistrationCertificate')}
                                                                            certDescription={t('mall.businessRegistrationCertificateDescription')}
                                                                            existingCertUrl={businessData.businessRegistrationCertificateUrl}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.mailOrderSalesNumber')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.mailOrderSalesRegistrationNumber}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <CertFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'mailOrderSalesRegistrationNumber'}
                                                                            label={t('mall.mailOrderSalesNumber')}
                                                                            value={businessData.mailOrderSalesRegistrationNumber}
                                                                            certField={'mailOrderSalesRegistrationCertificateUrl'}
                                                                            certLabel={t('mall.mailOrderSalesRegistrationCertificate')}
                                                                            certDescription={t('mall.mailOrderSalesRegistrationCertificateDescription')}
                                                                            existingCertUrl={businessData.mailOrderSalesRegistrationCertificateUrl}
                                                                            transform={normalizeOnlineSalesNumber}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.managerPhoneNumber')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.managerPhoneNumber}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <ManagerPhoneDialog
                                                                            mallId={mallId || ''}
                                                                            managerPhoneNumber={businessData.managerPhoneNumber}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.companyAddress')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.companyAddress}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <SingleFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'companyAddress'}
                                                                            label={t('mall.companyAddress')}
                                                                            value={businessData.companyAddress}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <LabeledTextListItem
                                                        style={{ minWidth: '120px' }}
                                                        isBlur={isBlurData}
                                                        label={t('mall.customerCenterNumber')}
                                                        content={
                                                            <EditableValue
                                                                value={businessData.customerCenterNumber}
                                                                onOpen={() =>
                                                                    openEditDialog(
                                                                        <SingleFieldDialog
                                                                            mallId={mallId || ''}
                                                                            field={'customerCenterNumber'}
                                                                            label={t('mall.customerCenterNumber')}
                                                                            value={businessData.customerCenterNumber}
                                                                        />,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                    />
                                                </LabeledTextList>
                                            </CardContent>
                                        </Card>
                                    );
                                }}
                            </Await>
                        </Suspense>
                    </Grid>
                    <Grid xs={12}>
                        <Card>
                            <div className="flex">
                                <CardHeader
                                    title={
                                        <div className={'flex justify-between items-center'}>
                                            <p>{t('mall.returnAddressSettings')}</p>
                                            <Box
                                                sx={{
                                                    height: '10px',
                                                    overflowY: 'visible',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => {
                                                        dialogOpen({
                                                            onExited: () => {
                                                                revalidate();
                                                            },
                                                            innerElement: (
                                                                <ReturnAddressInfoDialog
                                                                    mallId={mallId || ''}
                                                                    postCode={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.postCode || ''
                                                                    }
                                                                    recipientAddressDetail={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.recipientAddressDetail || ''
                                                                    }
                                                                    recipientAddress={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.recipientAddress || ''
                                                                    }
                                                                    returnShippingCost={String(
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnShippingCost || 0,
                                                                    )}
                                                                    returnAddressName={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnAddressName || ''
                                                                    }
                                                                    returnAddressTel={
                                                                        brandInfoData?.mallReturnAddressInfo
                                                                            ?.returnAddressTel || ''
                                                                    }
                                                                />
                                                            ),
                                                        });
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Box>
                                        </div>
                                    }
                                />
                            </div>
                            <CardContent>
                                <LabeledTextList>
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.address')}
                                        content={brandInfoData?.mallReturnAddressInfo?.recipientAddress || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.detailedAddress')}
                                        content={brandInfoData?.mallReturnAddressInfo?.recipientAddressDetail || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.postalCode')}
                                        content={brandInfoData?.mallReturnAddressInfo?.postCode || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnAddressName')}
                                        content={brandInfoData?.mallReturnAddressInfo?.returnAddressName || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnAddressPhone')}
                                        content={brandInfoData?.mallReturnAddressInfo?.returnAddressTel || '-'}
                                    />
                                    <LabeledTextListItem
                                        style={{ minWidth: '120px' }}
                                        label={t('mall.returnShippingCost')}
                                        content={
                                            !brandInfoData?.mallReturnAddressInfo?.returnShippingCost
                                                ? t('mall.freeShippingSellerBears')
                                                : `${Number(brandInfoData?.mallReturnAddressInfo?.returnShippingCost || 0).toLocaleString()}${t('common.koreanWon')}`
                                        }
                                    />
                                </LabeledTextList>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid xs={12}>
                        <Suspense fallback={<div>loading...</div>}>
                            <Await resolve={businessData}>
                                {(businessData) => {
                                    console.log(businessData);
                                    if (businessData.mallOpenBusinessType === 'SELLER') return;
                                    return (
                                        <Box display={'flex'} flexDirection={'column'} justifyContent={'center'}>
                                            <Box display={'flex'} alignItems={'center'}>
                                                {/* <InfoIcon color="error" sx={{ mr: 1 }} /> */}
                                                <Typography variant="h5">
                                                    {t('mall.businessRegistrationRecommendation')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    size="small"
                                                    color="black"
                                                    sx={{ bgcolor: 'lightgray' }}
                                                    variant="outlined"
                                                    onClick={() => {
                                                        dialogOpen({
                                                            innerElement: <TaxInfoDialog />,
                                                            fullWidth: true,
                                                        });
                                                    }}
                                                >
                                                    <Typography>
                                                        {t('mall.taxGuidanceForUnregisteredBusiness')}
                                                    </Typography>
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            </Await>
                        </Suspense>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};

export default BusinessInfo;
